import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Button, Skeleton, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { RosterGroup } from '@/model';
import { RosterGroupFilters } from './types';
import { RosterGroupTable } from './components/RosterGroupTable/RosterGroupTable';
import { RosterGroupFilterBox } from './components/RosterGroupFilterBox';
import {
  useDeleteRosterGroupMutation,
  useGetRosterGroupById,
  useReorderRosterGroupsMutation,
  useRosterGroup,
} from '@/services/queries/roster-group';
import { RosterGroupCreateModal } from './components/RosterGroupCreateModal';
import { RosterGroupEditModal } from './components/RosterGroupEditModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useAllClub } from '@/services/queries/club';

export const ApplicationRosterGroup = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.ROSTER);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_roster_group.application_roster_group_enable_success')
      : t('pages.application_roster_group.application_roster_group_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_roster_group.application_roster_group_enable_error')
      : t('pages.application_roster_group.application_roster_group_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.ROSTER, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<RosterGroup | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeleteRosterGroupMutation();
  const reorderMutation = useReorderRosterGroupsMutation();
  const getItemByUUID = useGetRosterGroupById();

  const filtersForm = useFiltersForm<RosterGroupFilters>({
    defaultValues: {
      team_uuid: '',
      name: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting] = useState<{ field: string; desc: boolean }>({ field: 'sort_order', desc: false });
  const [pagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 100,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useRosterGroup(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: RosterGroup) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_roster_group.application_roster_group_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_roster_group.application_roster_group_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_roster_group.application_roster_group_edit_error'),
        });
      },
    });
  };

  const onHandleReorder = useCallback(
    (orderedUuids: string[]) => {
      reorderMutation.mutate(
        { items: orderedUuids.map((uuid, index) => ({ uuid, sort_order: index })) },
        {
          onSuccess: () => {
            forceRefetch();
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_roster_group.application_roster_group_reorder_error'),
            });
            forceRefetch();
          },
        },
      );
    },
    [reorderMutation, forceRefetch, toast, t],
  );

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_roster_group.application_roster_group_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_roster_group.application_roster_group_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_roster_group.application_roster_group_enabled')
                    : t('pages.application_roster_group.application_roster_group_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_roster_group.application_roster_group_add_button')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <RosterGroupFilterBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <RosterGroupTable
            data={itemsData}
            isLoading={isLoading}
            isReorderInFlight={reorderMutation.isLoading}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
            onReorder={onHandleReorder}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <RosterGroupEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <RosterGroupCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_roster_group.application_roster_group_delete_confirm_title')}
        description={
          <Text>
            {t('pages.application_roster_group.application_roster_group_delete_confirm_description_start')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.name}"
            </Text>
            {t('pages.application_roster_group.application_roster_group_delete_confirm_description_end')}
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_roster_group.application_roster_group_toggle_confirm_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_roster_group.application_roster_group_enable_confirm_description')
              : t('pages.application_roster_group.application_roster_group_disable_confirm_description')}
          </Text>
        }
      />

      <CenteredSpinner
        isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading}
      />
    </PageContent>
  );
};
